import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import { ACTIVE_THEME, getThemeVars } from '../themes/dashboardThemes';
import ResponsiveDashboardShell from '../components/layout/ResponsiveDashboardShell';
import { communityService, searchService } from '../services/api';
import { useCategories } from '../hooks/useCategories';
import { useAuth } from '../hooks/useAuth';
import { useWishlist } from '../hooks/useWishlist';
import { formatCurrency, formatNumber } from '../utils/i18n';
import { getImageUrl } from '../utils/imageUtils';

const DEFAULT_FILTERS = {
  categoryId: '',
  condition: '',
  communityId: '',
  minPrice: '',
  maxPrice: '',
  location: '',
  sortBy: 'recent',
  onlyAvailable: false,
};

const PAGE_SIZE = 12;

function SearchNew() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { t } = useTranslation();
  const theme = getThemeVars(ACTIVE_THEME);
  const [isPhoneLayout, setIsPhoneLayout] = useState(() => window.matchMedia('(max-width:799px)').matches);
  const [supportsViewMode, setSupportsViewMode] = useState(() => window.matchMedia('(min-width:600px)').matches);

  useEffect(() => {
    const phoneQuery = window.matchMedia('(max-width:799px)');
    const viewQuery = window.matchMedia('(min-width:600px)');
    const onPhoneChange = (e) => setIsPhoneLayout(e.matches);
    const onViewChange = (e) => setSupportsViewMode(e.matches);
    phoneQuery.addEventListener('change', onPhoneChange);
    viewQuery.addEventListener('change', onViewChange);
    return () => {
      phoneQuery.removeEventListener('change', onPhoneChange);
      viewQuery.removeEventListener('change', onViewChange);
    };
  }, []);

  const [products, setProducts] = useState([]);
  const [communities, setCommunities] = useState([]);
  const { categories } = useCategories();
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [showFilters, setShowFilters] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const { wishlistStatusMap, pendingProductIds, toggleWishlist: toggleWishlistItem } = useWishlist();

  const conditions = [
    { value: 'NEW', label: t('Nuevo') },
    { value: 'LIKE_NEW', label: t('Como nuevo') },
    { value: 'USED', label: t('Usado') },
  ];

  const sortOptions = [
    { value: 'recent', label: t('Mas recientes') },
    { value: 'price_asc', label: t('Precio: menor a mayor') },
    { value: 'price_desc', label: t('Precio: mayor a menor') },
    { value: 'views', label: t('Mas vistos') },
    { value: 'name', label: t('Nombre: A-Z') },
  ];

  const readFiltersFromParams = useCallback(() => ({
    categoryId: searchParams.get('categoryId') || '',
    condition: searchParams.get('condition') || '',
    communityId: searchParams.get('communityId') || '',
    minPrice: searchParams.get('minPrice') || '',
    maxPrice: searchParams.get('maxPrice') || '',
    location: searchParams.get('location') || '',
    sortBy: searchParams.get('sortBy') || DEFAULT_FILTERS.sortBy,
    onlyAvailable: searchParams.get('onlyAvailable') === 'true',
  }), [searchParams]);

  const buildSearchParams = useCallback((query, nextFilters) => {
    const params = new URLSearchParams();

    if (query) {
      params.set('q', query);
    }

    Object.entries(nextFilters).forEach(([key, value]) => {
      if (value === '' || value === false || value === null || value === undefined) {
        return;
      }
      params.set(key, String(value));
    });

    return params;
  }, []);

  const applySearch = useCallback((query, nextFilters) => {
    setSearchQuery(query);
    setFilters(nextFilters);
    setSearchParams(buildSearchParams(query, nextFilters));
    setPage(1);
  }, [buildSearchParams, setSearchParams]);

  /* ── Load communities (categorías vienen del cache via useCategories) ── */
  useEffect(() => {
    let isMounted = true;
    communityService.getCommunities()
      .then(r => { if (isMounted) setCommunities(r.data || []); })
      .catch(err => { console.error('[SearchNew] error cargando comunidades:', err); });
    return () => { isMounted = false; };
  }, []);

  /* ── Sync state from URL params ── */
  useEffect(() => {
    setSearchQuery(searchParams.get('q') || '');
    setFilters(readFiltersFromParams());
    setPage(1);
  }, [readFiltersFromParams, searchParams]);

  useEffect(() => {
    setShowFilters(!isPhoneLayout);
  }, [isPhoneLayout]);

  /* ── Perform search ── */
  useEffect(() => {
    const performSearch = async () => {
      setLoading(true);
      try {
        const query = searchParams.get('q') || '';
        const activeFilters = readFiltersFromParams();
        const backendFilters = {};

        if (activeFilters.onlyAvailable) {
          backendFilters.status = 'AVAILABLE';
        }
        if (activeFilters.minPrice !== '') {
          backendFilters.minPrice = activeFilters.minPrice;
        }
        if (activeFilters.maxPrice !== '') {
          backendFilters.maxPrice = activeFilters.maxPrice;
        }
        if (activeFilters.categoryId) {
          backendFilters.categoryId = activeFilters.categoryId;
        }
        if (activeFilters.condition) {
          backendFilters.condition = activeFilters.condition;
        }

        const response = await searchService.searchProducts(query, backendFilters);
        const data = response.data || {};
        let results = data.results || [];

        if (activeFilters.communityId) {
          results = results.filter((product) => String(product.communityId) === String(activeFilters.communityId));
        }

        if (activeFilters.location) {
          const normalizedLocation = activeFilters.location.toLowerCase();
          results = results.filter((product) =>
            String(product.location || '').toLowerCase().includes(normalizedLocation)
          );
        }

        results = [...results].sort((left, right) => {
          switch (activeFilters.sortBy) {
            case 'price_asc':
              return Number(left.price || 0) - Number(right.price || 0);
            case 'price_desc':
              return Number(right.price || 0) - Number(left.price || 0);
            case 'views':
              return Number(right.viewCount || 0) - Number(left.viewCount || 0);
            case 'name':
              return String(left.name || '').localeCompare(String(right.name || ''));
            case 'recent':
            default:
              return new Date(right.createdAt || 0).getTime() - new Date(left.createdAt || 0).getTime();
          }
        });

        const computedTotalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
        const safePage = Math.min(page, computedTotalPages);

        if (safePage !== page) {
          setPage(safePage);
          return;
        }

        const startIndex = (safePage - 1) * PAGE_SIZE;
        setProducts(results.slice(startIndex, startIndex + PAGE_SIZE));
        setTotalPages(computedTotalPages);
        setTotalResults(results.length);
      } catch (error) {
        setProducts([]);
        setTotalPages(1);
        setTotalResults(0);
      } finally {
        setLoading(false);
      }
    };

    performSearch();
  }, [page, readFiltersFromParams, searchParams]);

  /* ── Suggestions (debounced) ── */
  const loadSuggestions = useMemo(() =>
    debounce(async (query) => {
      if (query.length < 2) {
        setSuggestions([]);
        return;
      }
      try {
        const response = await searchService.getSearchSuggestions(query);
        const data = response.data || {};
        setSuggestions(data.suggestions || []);
      } catch (error) {
      }
    }, 300), []);

  useEffect(() => () => loadSuggestions.cancel(), [loadSuggestions]);

  /* ── Handlers ── */
  const handleSearch = () => {
    setShowSuggestions(false);
    applySearch(searchQuery, filters);
    if (isPhoneLayout) {
      setShowFilters(false);
    }
  };

  const handleFilterChange = (field) => (event) => {
    const value = event.target.value;
    setFilters((previous) => ({ ...previous, [field]: value }));
  };

  const handleClearFilters = () => {
    applySearch('', DEFAULT_FILTERS);
    if (isPhoneLayout) {
      setShowFilters(false);
    }
  };

  const toggleWishlist = async (product) => {
    if (!user) {
      navigate('/login', { state: { from: '/search' } });
      return;
    }
    try {
      await toggleWishlistItem(product.id, product);
    } catch (error) {
    }
  };

  const formatPrice = (price) => formatCurrency(price, { currency: 'EUR' });

  const getConditionInfo = (condition) => {
    const map = {
      NEW: { label: t('Nuevo'), cls: 'bg-green-100 text-green-800' },
      LIKE_NEW: { label: t('Como nuevo'), cls: 'bg-blue-100 text-blue-800' },
      USED: { label: t('Usado'), cls: 'bg-amber-100 text-amber-800' },
    };
    return map[condition] || { label: condition, cls: 'bg-gray-100 text-gray-800' };
  };

  const getStatusInfo = (status) => {
    const map = {
      AVAILABLE: { label: t('Disponible'), cls: 'bg-green-100 text-green-800' },
      RESERVED: { label: t('Reservado'), cls: 'bg-amber-100 text-amber-800' },
      SOLD: { label: t('Vendido'), cls: 'bg-red-100 text-red-800' },
    };
    return map[status] || { label: status, cls: 'bg-gray-100 text-gray-800' };
  };

  const effectiveViewMode = supportsViewMode ? viewMode : 'grid';

  /* ── Active filter chips ── */
  const activeChips = [];
  if (searchQuery) {
    activeChips.push({ key: 'query', label: `${t('Busqueda')}: "${searchQuery}"`, onRemove: () => applySearch('', filters) });
  }
  if (filters.categoryId) {
    const cat = categories.find((c) => String(c.id) === String(filters.categoryId));
    activeChips.push({ key: 'cat', label: `${t('Categoria')}: ${cat?.name || filters.categoryId}`, onRemove: () => applySearch(searchQuery, { ...filters, categoryId: '' }) });
  }
  if (filters.condition) {
    const cond = conditions.find((c) => c.value === filters.condition);
    activeChips.push({ key: 'cond', label: `${t('Condicion')}: ${cond?.label || filters.condition}`, onRemove: () => applySearch(searchQuery, { ...filters, condition: '' }) });
  }
  if (filters.communityId) {
    const comm = communities.find((c) => String(c.id) === String(filters.communityId));
    activeChips.push({ key: 'comm', label: `${t('Comunidad')}: ${comm?.name || filters.communityId}`, onRemove: () => applySearch(searchQuery, { ...filters, communityId: '' }) });
  }
  if (filters.minPrice || filters.maxPrice) {
    activeChips.push({ key: 'price', label: `${t('Precio')}: $${filters.minPrice || 0} - $${filters.maxPrice || '\u221e'}`, onRemove: () => applySearch(searchQuery, { ...filters, minPrice: '', maxPrice: '' }) });
  }
  if (filters.location) {
    activeChips.push({ key: 'loc', label: `${t('Ubicacion')}: ${filters.location}`, onRemove: () => applySearch(searchQuery, { ...filters, location: '' }) });
  }
  if (filters.onlyAvailable) {
    activeChips.push({ key: 'avail', label: t('Solo disponibles'), onRemove: () => applySearch(searchQuery, { ...filters, onlyAvailable: false }) });
  }

  /* ── Pagination helpers ── */
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, page - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  /* ── Product card (grid mode) ── */
  const renderGridCard = (product) => {
    const condInfo = getConditionInfo(product.condition);
    const statusInfo = getStatusInfo(product.status);
    const isInWishlist = wishlistStatusMap[product.id];
    const isPending = pendingProductIds[product.id];

    return (
      <div
        key={product.id}
        className="bg-surface-container-lowest rounded-xl shadow-sm overflow-hidden cursor-pointer hover:shadow-lg transition-all flex flex-col group"
        onClick={() => navigate(`/products/${product.id}`)}
      >
        {/* Image */}
        <div className="relative aspect-[4/3] overflow-hidden">
          <img
            src={product.imageUrl || getImageUrl(product.imageId) || '/placeholder-product.jpg'}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />

          {product.status !== 'AVAILABLE' && (
            <span className={`absolute top-2 left-2 px-2 py-0.5 rounded text-xs font-bold text-white ${product.status === 'RESERVED' ? 'bg-amber-500' : 'bg-red-500'}`}>
              {product.status === 'RESERVED' ? t('Reservado') : t('Vendido')}
            </span>
          )}

          {/* Wishlist heart */}
          <button
            onClick={(e) => { e.stopPropagation(); toggleWishlist(product); }}
            disabled={isPending}
            className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 hover:bg-white flex items-center justify-center transition-colors"
          >
            {isPending ? (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent" />
            ) : (
              <span
                className={`material-symbols-outlined text-[20px] ${isInWishlist ? 'text-red-500' : 'text-on-surface-variant'}`}
                style={isInWishlist ? { fontVariationSettings: "'FILL' 1" } : {}}
              >
                favorite
              </span>
            )}
          </button>

          {/* Views badge */}
          {(product.viewCount !== undefined || product.viewsCount !== undefined) && (
            <div className="absolute bottom-2 right-2 flex items-center gap-1 px-2 py-0.5 rounded bg-black/60 text-white text-xs">
              <span className="material-symbols-outlined text-[14px]">visibility</span>
              {formatNumber(product.viewCount || product.viewsCount || 0)}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-4 flex-1 flex flex-col">
          <h3 className="text-on-surface font-medium truncate">{product.name}</h3>
          <p className="text-lg font-bold text-primary mt-1">{formatPrice(product.price)}</p>

          <div className="flex gap-1.5 mt-2 flex-wrap">
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${condInfo.cls}`}>
              {condInfo.label}
            </span>
            {product.status !== 'AVAILABLE' && (
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.cls}`}>
                {statusInfo.label}
              </span>
            )}
            {product.communityName && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border border-outline-variant/30 text-on-surface-variant">
                {product.communityName}
              </span>
            )}
          </div>

          {product.location && (
            <div className="flex items-center gap-1 mt-2 text-xs text-on-surface-variant">
              <span className="material-symbols-outlined text-[14px]">location_on</span>
              {product.location}
            </div>
          )}
        </div>
      </div>
    );
  };

  /* ── Product card (list mode) ── */
  const renderListCard = (product) => {
    const condInfo = getConditionInfo(product.condition);
    const statusInfo = getStatusInfo(product.status);
    const isInWishlist = wishlistStatusMap[product.id];
    const isPending = pendingProductIds[product.id];

    return (
      <div
        key={product.id}
        className="bg-surface-container-lowest rounded-xl shadow-sm overflow-hidden cursor-pointer hover:shadow-lg transition-all flex flex-col mobile-sm:flex-row group"
        onClick={() => navigate(`/products/${product.id}`)}
      >
        {/* Image */}
        <div className="relative w-full mobile-sm:w-48 h-52 mobile-sm:min-h-[120px] mobile-sm:h-auto shrink-0 overflow-hidden">
          <img
            src={product.imageUrl || getImageUrl(product.imageId) || '/placeholder-product.jpg'}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
          {product.status !== 'AVAILABLE' && (
            <span className={`absolute top-2 left-2 px-2 py-0.5 rounded text-xs font-bold text-white ${product.status === 'RESERVED' ? 'bg-amber-500' : 'bg-red-500'}`}>
              {product.status === 'RESERVED' ? t('Reservado') : t('Vendido')}
            </span>
          )}
        </div>

        {/* Content */}
        <div className="p-4 flex-1 flex flex-col min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h3 className="text-on-surface font-medium truncate text-base">{product.name}</h3>
              <p className="text-lg font-bold text-primary mt-0.5">{formatPrice(product.price)}</p>
            </div>

            {/* Wishlist heart */}
            <button
              onClick={(e) => { e.stopPropagation(); toggleWishlist(product); }}
              disabled={isPending}
              className="w-9 h-9 rounded-full hover:bg-surface-container flex items-center justify-center transition-colors shrink-0"
            >
              {isPending ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent" />
              ) : (
                <span
                  className={`material-symbols-outlined text-[20px] ${isInWishlist ? 'text-red-500' : 'text-on-surface-variant'}`}
                  style={isInWishlist ? { fontVariationSettings: "'FILL' 1" } : {}}
                >
                  favorite
                </span>
              )}
            </button>
          </div>

          <div className="flex gap-1.5 mt-2 flex-wrap">
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${condInfo.cls}`}>
              {condInfo.label}
            </span>
            {product.status !== 'AVAILABLE' && (
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.cls}`}>
                {statusInfo.label}
              </span>
            )}
            {product.communityName && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border border-outline-variant/30 text-on-surface-variant">
                {product.communityName}
              </span>
            )}
          </div>

          {product.description && (
            <p className="text-sm text-on-surface-variant mt-2 line-clamp-2">
              {product.description.substring(0, 150)}...
            </p>
          )}

          <div className="flex items-center gap-4 mt-auto pt-2 text-xs text-on-surface-variant">
            <div className="flex items-center gap-1">
              <span className="material-symbols-outlined text-[14px]">visibility</span>
              {t('{{count}} vistas', { count: formatNumber(product.viewCount || product.viewsCount || 0) })}
            </div>
            {product.location && (
              <div className="flex items-center gap-1">
                <span className="material-symbols-outlined text-[14px]">location_on</span>
                {product.location}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const filtersPanel = (
    <div className={`bg-surface-container-lowest rounded-xl shadow-sm p-5 ${isPhoneLayout ? 'h-full overflow-y-auto' : 'sticky top-6'}`}>
      {isPhoneLayout && (
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-on-surface">{t('Filtros')}</h2>
          <button
            onClick={() => setShowFilters(false)}
            className="w-9 h-9 rounded-full hover:bg-surface-container flex items-center justify-center"
          >
            <span className="material-symbols-outlined text-[20px]">close</span>
          </button>
        </div>
      )}

      {/* Category */}
      <div className="mb-4">
        <label className="block text-xs font-medium text-on-surface-variant mb-1.5">{t('Categoria')}</label>
        <select
          value={filters.categoryId}
          onChange={handleFilterChange('categoryId')}
          className="w-full border-2 border-outline-variant/30 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:border-primary transition-colors"
          style={{ color: 'var(--chrome-text)' }}
        >
          <option value="">{t('Todas las categorias')}</option>
          {categories.map((category) => (
            <option key={category.id} value={category.id}>{category.name}</option>
          ))}
        </select>
      </div>

      {/* Condition */}
      <div className="mb-4">
        <label className="block text-xs font-medium text-on-surface-variant mb-1.5">{t('Condicion')}</label>
        <select
          value={filters.condition}
          onChange={handleFilterChange('condition')}
          className="w-full border-2 border-outline-variant/30 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:border-primary transition-colors"
          style={{ color: 'var(--chrome-text)' }}
        >
          <option value="">{t('Cualquier condicion')}</option>
          {conditions.map((condition) => (
            <option key={condition.value} value={condition.value}>{condition.label}</option>
          ))}
        </select>
      </div>

      {/* Community */}
      <div className="mb-4">
        <label className="block text-xs font-medium text-on-surface-variant mb-1.5">{t('Comunidad')}</label>
        <select
          value={filters.communityId}
          onChange={handleFilterChange('communityId')}
          className="w-full border-2 border-outline-variant/30 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:border-primary transition-colors"
          style={{ color: 'var(--chrome-text)' }}
        >
          <option value="">{t('Todas las comunidades')}</option>
          {communities.map((community) => (
            <option key={community.id} value={community.id}>{community.name}</option>
          ))}
        </select>
      </div>

      {/* Price range */}
      <div className="mb-4">
        <label className="block text-xs font-medium text-on-surface-variant mb-1.5">{t('Rango de precios')}</label>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-on-surface-variant">$</span>
            <input
              type="number"
              value={filters.minPrice}
              onChange={(e) => setFilters((prev) => ({ ...prev, minPrice: e.target.value }))}
              placeholder={t('Min')}
              className="w-full border-2 border-outline-variant/30 rounded-lg pl-7 pr-2 py-2 text-sm bg-white focus:outline-none focus:border-primary transition-colors"
              style={{ color: 'var(--chrome-text)' }}
            />
          </div>
          <span className="flex items-center text-on-surface-variant text-sm">-</span>
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-on-surface-variant">$</span>
            <input
              type="number"
              value={filters.maxPrice}
              onChange={(e) => setFilters((prev) => ({ ...prev, maxPrice: e.target.value }))}
              placeholder={t('Max')}
              className="w-full border-2 border-outline-variant/30 rounded-lg pl-7 pr-2 py-2 text-sm bg-white focus:outline-none focus:border-primary transition-colors"
              style={{ color: 'var(--chrome-text)' }}
            />
          </div>
        </div>
      </div>

      {/* Location */}
      <div className="mb-4">
        <label className="block text-xs font-medium text-on-surface-variant mb-1.5">{t('Ubicacion')}</label>
        <div className="relative">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-[18px] text-on-surface-variant">location_on</span>
          <input
            type="text"
            value={filters.location}
            onChange={handleFilterChange('location')}
            placeholder={t('Ciudad, zona...')}
            className="w-full border-2 border-outline-variant/30 rounded-lg pl-9 pr-3 py-2 text-sm bg-white focus:outline-none focus:border-primary transition-colors"
            style={{ color: 'var(--chrome-text)' }}
          />
        </div>
      </div>

      {/* Only available */}
      <label className="flex items-center gap-2.5 mb-5 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={filters.onlyAvailable}
          onChange={(e) => setFilters((prev) => ({ ...prev, onlyAvailable: e.target.checked }))}
          className="w-4 h-4 rounded border-2 border-outline-variant/50 accent-primary"
        />
        <span className="text-sm text-on-surface">{t('Solo disponibles')}</span>
      </label>

      <div className="border-t border-outline-variant/20 my-4" />

      <div className="flex flex-col mobile-sm:flex-row gap-2">
        <button
          onClick={handleSearch}
          className="flex-1 py-2.5 rounded-lg bg-primary text-on-primary font-medium hover:opacity-90 transition-opacity"
        >
          {t('Aplicar filtros')}
        </button>
        <button
          onClick={handleClearFilters}
          className="flex-1 py-2.5 rounded-lg border-2 border-outline-variant/30 text-on-surface-variant font-medium hover:bg-surface-container transition-colors"
        >
          {t('Limpiar todo')}
        </button>
      </div>
    </div>
  );

  return (
    <ResponsiveDashboardShell
      activePath="/"
      theme={theme}
      withContentWrapper={false}
    >
      <div className="flex gap-0 min-h-full">

        {/* Left filter panel — desktop only */}
        <aside className="hidden tablet-md:block w-56 shrink-0 border-r border-outline-variant px-4 py-5">
          <h3 className="text-sm font-semibold text-on-surface mb-4">{t('Filters')}</h3>

          {/* Price range */}
          <div className="mb-5">
            <p className="text-xs font-semibold text-on-surface-variant uppercase tracking-wide mb-2">{t('Price')}</p>
            <div className="flex items-center gap-2">
              <input
                type="number"
                placeholder={t('Min')}
                value={filters.minPrice}
                onChange={(e) => setFilters((prev) => ({ ...prev, minPrice: e.target.value }))}
                className="w-full px-2 py-1.5 text-sm rounded border border-outline bg-surface text-on-surface outline-none focus:border-primary"
              />
              <span className="text-on-surface-variant text-sm">–</span>
              <input
                type="number"
                placeholder={t('Max')}
                value={filters.maxPrice}
                onChange={(e) => setFilters((prev) => ({ ...prev, maxPrice: e.target.value }))}
                className="w-full px-2 py-1.5 text-sm rounded border border-outline bg-surface text-on-surface outline-none focus:border-primary"
              />
            </div>
          </div>

          {/* Category */}
          <div className="mb-5">
            <p className="text-xs font-semibold text-on-surface-variant uppercase tracking-wide mb-2">{t('Category')}</p>
            <div className="flex flex-col gap-1.5">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="category" value="" checked={filters.categoryId === ''} onChange={() => setFilters((p) => ({ ...p, categoryId: '' }))} className="accent-primary w-4 h-4" />
                <span className="text-sm text-on-surface">{t('All')}</span>
              </label>
              {categories.map((cat) => (
                <label key={cat.id} className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="category" value={cat.id} checked={String(filters.categoryId) === String(cat.id)} onChange={() => setFilters((p) => ({ ...p, categoryId: String(cat.id) }))} className="accent-primary w-4 h-4" />
                  <span className="text-sm text-on-surface truncate">{cat.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Community */}
          {communities.length > 0 && (
            <div className="mb-5">
              <p className="text-xs font-semibold text-on-surface-variant uppercase tracking-wide mb-2">{t('Community')}</p>
              <div className="flex flex-col gap-1.5">
                {communities.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="accent-primary w-4 h-4"
                      checked={String(filters.communityId) === String(c.id)}
                      onChange={() => setFilters((p) => ({ ...p, communityId: String(filters.communityId) === String(c.id) ? '' : String(c.id) }))}
                    />
                    <span className="text-sm text-on-surface truncate">{c.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Condition */}
          <div className="mb-5">
            <p className="text-xs font-semibold text-on-surface-variant uppercase tracking-wide mb-2">{t('Condition')}</p>
            <div className="flex flex-col gap-1.5">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="condition" value="" checked={filters.condition === ''} onChange={() => setFilters((p) => ({ ...p, condition: '' }))} className="accent-primary w-4 h-4" />
                <span className="text-sm text-on-surface">{t('Any')}</span>
              </label>
              {conditions.map((cond) => (
                <label key={cond.value} className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="condition" value={cond.value} checked={filters.condition === cond.value} onChange={() => setFilters((p) => ({ ...p, condition: cond.value }))} className="accent-primary w-4 h-4" />
                  <span className="text-sm text-on-surface">{cond.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Only available */}
          <label className="flex items-center gap-2 cursor-pointer mb-5">
            <input type="checkbox" checked={filters.onlyAvailable} onChange={(e) => setFilters((p) => ({ ...p, onlyAvailable: e.target.checked }))} className="accent-primary w-4 h-4" />
            <span className="text-sm text-on-surface">{t('Available only')}</span>
          </label>

          <button onClick={handleSearch} className="w-full py-2 rounded-lg bg-primary text-on-primary text-sm font-semibold hover:opacity-90 transition-opacity mb-2">
            {t('Apply')}
          </button>
          <button onClick={handleClearFilters} className="w-full py-2 rounded-lg border border-outline text-on-surface-variant text-sm hover:bg-surface-container transition-colors">
            {t('Clear')}
          </button>
        </aside>

        {/* Results area */}
        <div className="flex-1 min-w-0 px-4 tablet-md:px-6 py-5">

              {/* Results header */}
              <div className="flex flex-col mobile-sm:flex-row mobile-sm:items-center mobile-sm:justify-between gap-3 mb-4">
                <h2 className="text-lg font-semibold text-on-surface">
                  {loading ? (
                    <span className="skeleton inline-block h-6 w-48 rounded" />
                  ) : (
                    t('{{count}} productos encontrados', { count: formatNumber(totalResults) })
                  )}
                </h2>

                <div className="flex flex-wrap items-center gap-3">
                  {/* Sort dropdown */}
                  <select
                    value={filters.sortBy}
                    onChange={handleFilterChange('sortBy')}
                    className="border-2 border-outline-variant/30 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:border-primary transition-colors"
                    style={{ color: 'var(--chrome-text)' }}
                  >
                    {sortOptions.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>

                  {/* Grid/List toggle */}
                  {supportsViewMode && (
                    <div className="flex rounded-lg border-2 border-outline-variant/30 overflow-hidden">
                      <button
                        onClick={() => setViewMode('grid')}
                        className={`w-9 h-9 flex items-center justify-center transition-colors ${viewMode === 'grid' ? 'bg-primary text-on-primary' : 'bg-white text-on-surface-variant hover:bg-surface-container'}`}
                      >
                        <span className="material-symbols-outlined text-[20px]">grid_view</span>
                      </button>
                      <button
                        onClick={() => setViewMode('list')}
                        className={`w-9 h-9 flex items-center justify-center transition-colors ${viewMode === 'list' ? 'bg-primary text-on-primary' : 'bg-white text-on-surface-variant hover:bg-surface-container'}`}
                      >
                        <span className="material-symbols-outlined text-[20px]">view_list</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* ── Loading skeletons ── */}
              {loading && (
                <div className={effectiveViewMode === 'grid'
                  ? 'grid grid-cols-2 tablet-sm:grid-cols-3 tablet-md:grid-cols-4 gap-4'
                  : 'flex flex-col gap-4'
                }>
                  {[...Array(PAGE_SIZE)].map((_, i) => (
                    <div key={i} className={`bg-surface-container-lowest rounded-xl shadow-sm overflow-hidden ${effectiveViewMode === 'list' ? 'flex flex-col mobile-sm:flex-row' : ''}`}>
                      <div className={`skeleton ${effectiveViewMode === 'grid' ? 'h-48 w-full' : 'w-full mobile-sm:w-48 h-[200px] mobile-sm:h-[120px]'}`} />
                      <div className="p-4 flex-1 space-y-2">
                        <div className="skeleton h-5 w-3/4 rounded" />
                        <div className="skeleton h-6 w-1/2 rounded" />
                        <div className="skeleton h-4 w-1/3 rounded" />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* ── Empty state ── */}
              {!loading && products.length === 0 && (
                <div className="bg-surface-container-lowest rounded-xl shadow-sm p-12 text-center">
                  <span className="material-symbols-outlined text-[64px] text-on-surface-variant/40">search_off</span>
                  <h3 className="text-lg font-semibold text-on-surface mt-4">
                    {t('No se encontraron productos')}
                  </h3>
                  <p className="text-on-surface-variant mt-2">
                    {t('No se encontraron productos. Intenta ajustar tus criterios de busqueda.')}
                  </p>
                  <button
                    onClick={handleClearFilters}
                    className="mt-6 px-6 py-2.5 rounded-lg bg-primary text-on-primary font-medium hover:opacity-90 transition-opacity"
                  >
                    {t('Limpiar filtros')}
                  </button>
                </div>
              )}

              {/* ── Product grid ── */}
              {!loading && products.length > 0 && effectiveViewMode === 'grid' && (
                <div className="grid grid-cols-2 tablet-sm:grid-cols-3 tablet-md:grid-cols-4 gap-4">
                  {products.map(renderGridCard)}
                </div>
              )}

              {/* ── Product list ── */}
              {!loading && products.length > 0 && effectiveViewMode === 'list' && (
                <div className="flex flex-col gap-4">
                  {products.map(renderListCard)}
                </div>
              )}

              {/* ── Pagination ── */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8">
                  <button
                    disabled={page === 1}
                    onClick={() => setPage((p) => p - 1)}
                    className="w-9 h-9 rounded-lg border-2 border-outline-variant/30 flex items-center justify-center hover:bg-surface-container transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <span className="material-symbols-outlined text-[18px]">chevron_left</span>
                  </button>

                  {getPageNumbers().map((num) => (
                    <button
                      key={num}
                      onClick={() => setPage(num)}
                      className={`w-9 h-9 rounded-lg text-sm font-medium flex items-center justify-center transition-colors ${
                        num === page
                          ? 'bg-primary text-on-primary'
                          : 'border-2 border-outline-variant/30 text-on-surface-variant hover:bg-surface-container'
                      }`}
                    >
                      {num}
                    </button>
                  ))}

                  <button
                    disabled={page === totalPages}
                    onClick={() => setPage((p) => p + 1)}
                    className="w-9 h-9 rounded-lg border-2 border-outline-variant/30 flex items-center justify-center hover:bg-surface-container transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <span className="material-symbols-outlined text-[18px]">chevron_right</span>
                  </button>
                </div>
              )}
        </div>

      </div>
    </ResponsiveDashboardShell>
  );
}

export default SearchNew;
